import { EventEmitter, Injectable } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { TranslationService } from "@core-services/translation.service";
import { Helper } from "@helpers/functions";
import { VerificationApplication } from "@models/verification_application";
import { OnfidoTranslations } from "../locale/onfido-translations";
import { ServiceNames } from "@models/service_enums";
import * as Onfido from "onfido-sdk-ui";

@Injectable({
	providedIn: "root"
})
export class OnfidoService {
	public readonly name: string = ServiceNames.ONFIDO;

	// Storing the init result
	// TODO: eslint fix
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private onfidoInstance: any = null;

	private documentType: string = "";

	// The DOM element needed by Onfido:
	private readonly containerId: string = "onfido-mount";

	// If the user closes the Onfido popup, we want to start the verification process directly at Onfido
	private hasEverStarted: boolean = false;
	private preselectedLanguage: string = "";

	private apiData: VerificationApplication = null;
	private finishedVerification: boolean = false;
	private verificationFail: boolean = false;

	// Incompatibility alternatives
	public static chromeApps: string = "use_chrome_or_apps";
	public static iosApp: string = "use_ios_app";

	private modalCloseClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

	public constructor(
		private apicallService: ApicallService,
		private translationService: TranslationService
	) {}

	public initOnfido(document: string = "passport"): void {
		this.hasEverStarted = true;
		const lang = this.getLanguage();
		this.documentType = document;

		this.apicallService.get("verification/onfido/" + lang, new ApicallOption(VerificationApplication)).subscribe(
			(res) => {
				this.apiData = res[0];
				this.startOnfido();
			},
			(error) => Helper.handleError(error)
		);
	}

	private startOnfido(): void {
		if (!this.apiData) {
			this.initOnfido();
			return;
		}

		// Init Onfido SDK
		this.onfidoInstance = Onfido.init({
			token: this.apiData.getToken(), // SDK token
			containerId: this.getContainerId(), // Id of the element you want to mount the component on:
			language: this.getLanguagePhrases(),
			useModal: true,
			isModalOpen: true,
			steps: [
				{
					type: "face",
					options: {
						requestedVariant: "video",
						uploadFallback: false
					}
				},
				{
					type: "document",
					options: {
						documentTypes: {
							passport: this.isDocumentType("passport"),
							national_identity_card: this.isDocumentType("id_card"),
							driving_licence: false
						},
						forceCrossDevice: true,
						useLiveDocumentCapture: true,
						uploadFallback: false,
						showCountrySelection: false
					}
				},
				"complete"
			],

			onModalRequestClose: () => {
				this.onCloseOnfidoModal();
			},

			onComplete: (data) => {
				// Callback for when everything is complete
				console.log("[Onfido] verification finished");
				this.finishedVerification = true;
				this.notifyApiFinish();
			}
		});
	}

	public getContainerId(): string {
		return this.containerId;
	}

	public getHasEverStarted(): boolean {
		return this.hasEverStarted;
	}

	public preselectLanguage(lang: string): void {
		this.preselectedLanguage = lang;
	}

	private isDocumentType(type: string): boolean {
		return this.documentType === type;
	}

	private getLanguage(): string {
		if (this.preselectedLanguage) {
			return this.preselectedLanguage;
		}
		return this.translationService.getLanguage();
	}

	private onCloseOnfidoModal(): void {
		if (this.finishedVerification) {
			// Jump directly to Onfido close
			this.onModalCloseResponse(true);
		} else {
			// Show confirm close modal
			this.modalCloseClicked.emit(true);
		}
	}

	public getModalCloseClick(): EventEmitter<boolean> {
		return this.modalCloseClicked;
	}

	public onModalCloseResponse(close: boolean): void {
		this.modalCloseClicked.emit(false); // Close confirm popup

		if (close) {
			// Update options with the state of the modal
			this.onfidoInstance.setOptions({
				isModalOpen: false
			});
			if (this.finishedVerification && !this.verificationFail) {
				window.location.reload();
			}
		}
	}

	private notifyApiFinish(): void {
		const a = new ApicallOption(null, { event: "verification_finished" });

		this.apicallService.post("verification/onfido/update_status", a).subscribe(
			(res) => {
				// Do nothing on ok
			},
			(error) => Helper.handleError(error)
		);
	}

	public static hasFullSupport(): boolean {
		return (
			this.hasVideoSupport() &&
			this.hasMimeSupport() &&
			(!this.isIOS() || (this.isIOS() && this.hasMinIOSVersion()))
		);
	}

	public static hasVideoSupport(): boolean {
		return navigator.mediaDevices || ["mediaDevices"]["getUserMedia"] || window["MediaRecorder"];
	}

	public static hasMimeSupport(): boolean {
		const mimeTypes = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm", "video/mp4"];
		return mimeTypes.some((type) => window["MediaRecorder"] && window["MediaRecorder"].isTypeSupported(type));
	}

	public static getIncompatibilityType(): string {
		if (!this.hasVideoSupport()) {
			return this.chromeApps;
		}
		if (!this.hasMimeSupport()) {
			return this.iosApp;
		}
		return "";
	}

	// TODO: eslint fix
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private getLanguagePhrases(): any {
		return OnfidoTranslations[this.getLanguage()];
	}

	public setVerificationFail(b: boolean): void {
		this.verificationFail = b;
	}

	public removeOnfido(): void {
		this.onfidoInstance.tearDown();
	}

	/**
	 * #2239: on iOS > 14.3 the user is
	 * explicitly allowed to proceed on web.
	 * Since the MimeType-check always fails,
	 * but works anyway we gonna check for the
	 * minimum iOS-version instead.
	 *
	 * @private
	 */
	private static hasMinIOSVersion(): boolean {
		const iosVersion = this.getIOSVersion();
		return iosVersion >= 14.3;
	}

	private static isIOS(): boolean {
		return !window["MSStream"] && /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
	}

	private static getIOSVersion(): number {
		const userAgent = navigator.userAgent;
		const hasOSInformation = userAgent.indexOf("OS ");
		let iosVersion;

		if (this.isIOS() && hasOSInformation) {
			try {
				iosVersion = +userAgent
					.substr(hasOSInformation + 3, 4)
					.replace("_", ".")
					.trim();
			} catch (e) {
				// Suppress error and return 'null'
				console.warn("[iOS-Detection] could not detect iOS version", e);
			}
		}
		return iosVersion;
	}
}
